import { C } from './palette';
import { bake, flip, type Sprite } from './sprites';

/** Change these two lines to rename the game. */
export const GAME_TITLE = 'UPHILL';
/** Drawn in the pixel font, so upper case only. */
export const GAME_SUBTITLE = 'SIERRA NEVADA FIRE RESCUE';
/** Used for the browser tab, where normal case reads better. */
export const GAME_TAGLINE = 'Sierra Nevada fire rescue';

// ---------------------------------------------------------------------------
// The flame: logo and favicon
// ---------------------------------------------------------------------------

const FLAME_RAMP = ['#a80000', '#ff0000', '#ff8700', '#ffd700', '#ffffff'];

/**
 * The flame is generated rather than hand-drawn so the same shape works at
 * favicon size and blown up as a logo. Narrow pointed tip, wide rounded base,
 * hot core that does not reach the top.
 */
export function drawFlame(ctx: CanvasRenderingContext2D, ox: number, oy: number, size: number, px = 1): void {
  const maxW = size * 0.42;
  for (let row = 0; row < size; row++) {
    const t = (row + 0.5) / size;
    // Width profile: widest near the base, narrower again at the very bottom.
    const half = maxW * Math.pow(t, 0.62) * (1 - 0.3 * Math.pow(t, 8));
    if (half < 0.5) continue;
    // A gentle lean, so it looks like a flame rather than a balloon.
    const cx = size / 2 + Math.sin(t * Math.PI * 1.15) * size * 0.06 - size * 0.02;
    // The hot core fades in below the tip.
    const core = Math.max(0, Math.min(1, (t - 0.12) / 0.45));

    for (let col = 0; col < size; col++) {
      const d = Math.abs(col + 0.5 - cx) / half;
      if (d > 1) continue;
      let band: number;
      if (d > 0.82) band = 0;
      else if (d > 0.6) band = 1;
      else if (d > 0.4) band = 2;
      else if (d > 0.2) band = 3 - (1 - core) * 1;
      else band = 4 - (1 - core) * 2;
      ctx.fillStyle = FLAME_RAMP[Math.max(0, Math.min(4, Math.round(band)))];
      ctx.fillRect(ox + col * px, oy + row * px, px, px);
    }
  }
}

/** Render the flame once and give it to the browser as the tab icon. */
export function installFavicon(size = 32): void {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d')!;
  drawFlame(ctx, 0, 0, size);
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.type = 'image/png';
  link.href = c.toDataURL('image/png');
}

// ---------------------------------------------------------------------------
// A 5x7 pixel font, so the title matches everything else on screen
// ---------------------------------------------------------------------------

const FONT: Record<string, string> = {
  A: '.###./#...#/#...#/#####/#...#/#...#/#...#',
  B: '####./#...#/#...#/####./#...#/#...#/####.',
  C: '.####/#..../#..../#..../#..../#..../.####',
  D: '####./#...#/#...#/#...#/#...#/#...#/####.',
  E: '#####/#..../#..../####./#..../#..../#####',
  F: '#####/#..../#..../####./#..../#..../#....',
  G: '.####/#..../#..../#.###/#...#/#...#/.###.',
  H: '#...#/#...#/#...#/#####/#...#/#...#/#...#',
  I: '#####/..#../..#../..#../..#../..#../#####',
  J: '....#/....#/....#/....#/#...#/#...#/.###.',
  K: '#...#/#..#./#.#../##.../#.#../#..#./#...#',
  L: '#..../#..../#..../#..../#..../#..../#####',
  M: '#...#/##.##/#.#.#/#...#/#...#/#...#/#...#',
  N: '#...#/##..#/#.#.#/#..##/#...#/#...#/#...#',
  O: '.###./#...#/#...#/#...#/#...#/#...#/.###.',
  P: '####./#...#/#...#/####./#..../#..../#....',
  Q: '.###./#...#/#...#/#...#/#.#.#/#..#./.##.#',
  R: '####./#...#/#...#/####./#.#../#..#./#...#',
  S: '.####/#..../#..../.###./....#/....#/####.',
  T: '#####/..#../..#../..#../..#../..#../..#..',
  U: '#...#/#...#/#...#/#...#/#...#/#...#/.###.',
  V: '#...#/#...#/#...#/#...#/#...#/.#.#./..#..',
  W: '#...#/#...#/#...#/#...#/#.#.#/##.##/#...#',
  X: '#...#/#...#/.#.#./..#../.#.#./#...#/#...#',
  Y: '#...#/#...#/.#.#./..#../..#../..#../..#..',
  Z: '#####/....#/...#./..#../.#.../#..../#####',
  '-': '·····/·····/·····/#####/·····/·····/·····'.replace(/·/g, '.'),
  '.': '·····/·····/·····/·····/·····/·····/..#..'.replace(/·/g, '.'),
  ' ': '...../...../...../...../...../...../.....',
};

export const textWidth = (text: string, scale: number): number => text.length * 6 * scale - scale;

export function drawPixelText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  scale: number,
  colour: string,
): void {
  ctx.fillStyle = colour;
  let cx = x;
  for (const ch of text.toUpperCase()) {
    const glyph = FONT[ch];
    if (glyph) {
      const rows = glyph.split('/');
      for (let r = 0; r < rows.length; r++) {
        for (let c = 0; c < rows[r].length; c++) {
          if (rows[r][c] !== '#') continue;
          ctx.fillRect(cx + c * scale, y + r * scale, scale, scale);
        }
      }
    }
    cx += 6 * scale;
  }
}

// ---------------------------------------------------------------------------
// Sprites for the attract screen, the demo that runs before a game starts
// ---------------------------------------------------------------------------

const FIREFIGHTER = bake(
  [
    '..YY..',
    '.YYYY.',
    '..WW..',
    '.BBBB.',
    '.BYYB.',
    '.BBBB.',
    '.BB.BB',
    '.B..B.',
    '.K..K.',
  ],
  { Y: C.yellowB, W: C.whiteB, B: '#1b2a6b', K: '#0a0a12' },
);

const cheering = (body: string): Sprite =>
  bake(
    [
      // Arms taper in to the shoulders. Detached pixels at the top corners
      // read as antennae, which made the villagers look like little aliens.
      'C.....C',
      '.C...C.',
      '..WWW..',
      '..WWW..',
      'CCCCCCC',
      '.CCCCC.',
      '..CCC..',
      '..C.C..',
      '..K.K..',
    ],
    { C: body, W: C.whiteB, K: '#0a0a12' },
  );

const VILLAGERS = [
  cheering(C.cyanB),
  cheering(C.magentaB),
  cheering(C.yellowB),
  cheering(C.greenB),
  cheering(C.whiteB),
];

const ENGINE_BIG = bake(
  [
    '..B......B..',
    '.RRRRRRRRRR.',
    'RRWWWRRRRRRR',
    'RRWWWRRRRRRR',
    'RRRRRRRRRRRR',
    '.KK...KK..KK',
  ],
  { R: C.red, W: C.cyanB, K: '#0a0a12', B: C.blueB },
);

const ENGINE_BIG_L = flip(ENGINE_BIG);

const HELI_BIG = bake(
  [
    '..........RRRR....',
    '......RRRRHHHHHH..',
    'TTTTKHHHHHHHHHHHGG',
    'TTTTKHHHHHHHHHHHGG',
    'TTTTKHHHHHHHHHHHG.',
    '.....KHHHHHHHHHH..',
    '.........MHHHM....',
  ],
  { H: C.red, R: '#ff4444', K: '#3a0000', T: C.whiteB, G: C.cyanB, M: '#57576e' },
);

// ---------------------------------------------------------------------------
// The scene
// ---------------------------------------------------------------------------

export const INTRO_W = 360;
export const INTRO_H = 200;

const hash = (a: number, b: number): number => {
  let h = (Math.imul(a, 374761393) + Math.imul(b, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) | 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

/** The switchbacks (hairpin bends) up the west side, as control points. */
const ROAD_PTS: readonly [number, number][] = [
  [0, 190],
  [58, 178],
  [26, 166],
  [92, 152],
  [48, 140],
  [120, 126],
];

const ROAD_SEGS = (() => {
  const segs: { x0: number; y0: number; x1: number; y1: number; len: number }[] = [];
  let total = 0;
  for (let i = 0; i < ROAD_PTS.length - 1; i++) {
    const [x0, y0] = ROAD_PTS[i];
    const [x1, y1] = ROAD_PTS[i + 1];
    const len = Math.hypot(x1 - x0, y1 - y0);
    segs.push({ x0, y0, x1, y1, len });
    total += len;
  }
  return { segs, total };
})();

/**
 * Where the crew's own appliance (their fire engine) is parked. On the last
 * bend, well left of where the fire starts, so nobody is standing in the
 * flames.
 */
const PARKED_U = 0.8;
/**
 * The moving engine works the lower switchbacks only, so it never drives over
 * the parked appliance or its crew further up.
 */
const PATROL_MAX = 0.45;

/** A point a fraction u along the road, so the engine drives the bends. */
function alongRoad(u: number): { x: number; y: number } {
  let d = Math.max(0, Math.min(1, u)) * ROAD_SEGS.total;
  for (const s of ROAD_SEGS.segs) {
    if (d <= s.len) {
      const f = s.len > 0 ? d / s.len : 0;
      return { x: s.x0 + (s.x1 - s.x0) * f, y: s.y0 + (s.y1 - s.y0) * f };
    }
    d -= s.len;
  }
  const last = ROAD_SEGS.segs[ROAD_SEGS.segs.length - 1];
  return { x: last.x1, y: last.y1 };
}

const FIRE_X0 = 96;
const FIRE_X1 = 232;

/**
 * The foot of the flames at a given column. The fire loop and the crew's hoses
 * both read this, so the jets are guaranteed to land in the fire rather than on
 * the hillside above it.
 */
function flameBase(x: number): number {
  return ridge(x) + 46 + Math.sin(x * 0.08) * 6;
}

/** Skyline of the drawn mountain: valley on the left, summit to the right. */
function ridge(x: number): number {
  const t = x / INTRO_W;
  const rise = 178 - 118 * Math.pow(Math.min(1, t / 0.78), 0.85);
  const fall = t > 0.78 ? (t - 0.78) * 150 : 0;
  return Math.round(rise + fall + Math.sin(x * 0.09) * 2.5 + Math.sin(x * 0.31) * 1.2);
}

const px = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, colour: string) => {
  ctx.fillStyle = colour;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
};

export function drawIntro(ctx: CanvasRenderingContext2D, t: number, audioBlocked = false): void {
  ctx.imageSmoothingEnabled = false;
  const frame = Math.floor(t * 12);

  // --- Sky, banded rather than smooth so it stays pixel art ----------------
  const sky = ['#080e3a', '#12174e', '#1c2062', '#2c2470', '#43276e', '#5c2c62', '#7a3350', '#9c4038', '#b85028'];
  for (let b = 0; b < sky.length; b++) {
    px(ctx, 0, (b * INTRO_H * 0.62) / sky.length, INTRO_W, INTRO_H * 0.62 / sky.length + 1, sky[b]);
  }
  // Sun sitting in the smoke, clear of the ridge line so it is actually seen.
  for (let r = 13; r > 0; r--) {
    ctx.fillStyle = r > 9 ? '#c2542066' : r > 5 ? '#e07a2099' : '#ffd700';
    ctx.beginPath();
    ctx.arc(330, 44, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // --- Far ridge -----------------------------------------------------------
  for (let x = 0; x < INTRO_W; x++) {
    const y = 118 + Math.sin(x * 0.045) * 9 + Math.sin(x * 0.017) * 6;
    px(ctx, x, y, 1, INTRO_H - y, '#2a1c3a');
  }

  // --- The mountain --------------------------------------------------------
  for (let x = 0; x < INTRO_W; x++) {
    const top = ridge(x);
    for (let y = top; y < INTRO_H; y++) {
      const depth = (y - top) / Math.max(1, INTRO_H - top);
      const n = hash(x, y);
      let colour: string;
      if (depth < 0.06) colour = n > 0.5 ? '#4a4a5e' : '#3a3a4e'; // rock at the top
      else if (depth < 0.4) colour = n > 0.6 ? '#00521a' : '#003d14'; // pine
      else if (depth < 0.72) colour = n > 0.6 ? '#52601a' : '#424e14'; // scrub
      else colour = n > 0.6 ? '#6b7233' : '#5a602a'; // terraces: flat farmed steps
      px(ctx, x, y, 1, 1, colour);
    }
    // Terrace lines low down.
    for (let y = Math.max(top + 4, 150); y < INTRO_H; y += 6) px(ctx, x, y, 1, 1, '#363a16');
  }

  // --- The road, switchbacking up the left flank ---------------------------
  ctx.strokeStyle = '#5e5e6e';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ROAD_PTS.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.stroke();

  // --- Fire across the lower slope ----------------------------------------
  const fireX0 = FIRE_X0;
  const fireX1 = FIRE_X1;
  for (let x = fireX0; x < fireX1; x++) {
    const base = flameBase(x);
    const flicker = hash(x, frame) * 0.6 + hash(x, frame + 1) * 0.4;
    const h = 8 + flicker * 22 + Math.sin(x * 0.21 + t * 3) * 4;
    for (let k = 0; k < h; k++) {
      const up = k / h;
      let c: string;
      if (up > 0.82) c = '#a80000';
      else if (up > 0.62) c = '#ff0000';
      else if (up > 0.38) c = '#ff8700';
      else if (up > 0.12) c = '#ffd700';
      // White is the hottest part of the flame, but a solid white line along
      // the whole base reads as a snow line. Break it up so it flickers.
      else c = hash(x, frame + 7) > 0.52 ? '#ffffff' : '#ffd700';
      px(ctx, x, base - k, 1, 1, c);
    }
    // Burnt ground behind the front (the fire's burning edge).
    for (let y = base + 1; y < base + 12; y++) px(ctx, x, y, 1, 1, hash(x, y) > 0.5 ? '#1a1a22' : '#24242c');
  }

  // --- Smoke ---------------------------------------------------------------
  for (let i = 0; i < 60; i++) {
    const seed = hash(i, 7);
    const life = (t * 0.22 + seed) % 1;
    const x = fireX0 + seed * (fireX1 - fireX0) + life * 70;
    const y = ridge(Math.round(Math.min(INTRO_W - 1, x))) + 42 - life * 90;
    const size = 2 + Math.round(life * 5);
    ctx.globalAlpha = 0.45 * (1 - life);
    px(ctx, x, y, size, size, seed > 0.5 ? '#2a2a32' : '#3c3c46');
  }
  ctx.globalAlpha = 1;

  // --- Fire engine working its way up the switchbacks ---------------------
  // Slowly: a full climb and descent takes about twenty seconds, and it stays
  // on the road rather than cutting across the mountain.
  const climb = t * 0.32;
  const u = ((1 - Math.cos(climb)) / 2) * PATROL_MAX;
  const goingUp = Math.sin(climb) > 0;
  const eng = alongRoad(u);
  const sprite = goingUp ? ENGINE_BIG : ENGINE_BIG_L;
  const ex = Math.round(eng.x - sprite.w / 2);
  const ey = Math.round(eng.y - sprite.h + 1);
  ctx.drawImage(sprite.canvas as CanvasImageSource, ex, ey);
  // Blue beacons alternating left and right, as they actually flash.
  const beat = Math.floor(t * 4) % 2 === 0;
  px(ctx, ex + (beat ? 1 : sprite.w - 3), ey - 2, 2, 2, C.blueB);
  px(ctx, ex + (beat ? 1 : sprite.w - 3), ey - 2, 1, 1, C.whiteB);

  // --- The appliance these two came in, parked on the road ----------------
  // Sits clear of the fire, uphill of the crew, blue beacon turning.
  const parked = alongRoad(PARKED_U);
  const parkX = Math.round(parked.x - ENGINE_BIG.w / 2);
  const parkY = Math.round(parked.y - ENGINE_BIG.h + 1);
  ctx.drawImage(ENGINE_BIG.canvas as CanvasImageSource, parkX, parkY);
  if (Math.floor(t * 3) % 2 === 0) {
    px(ctx, parkX + 2, parkY - 2, 2, 2, C.blueB);
    px(ctx, parkX + 2, parkY - 2, 1, 1, C.whiteB);
  } else {
    px(ctx, parkX + ENGINE_BIG.w - 4, parkY - 2, 2, 2, C.blueB);
  }

  // --- Crew working the flank, with a jet ---------------------------------
  // Downhill of their engine and to the LEFT of fireX0, so they are beside the
  // flames rather than standing in them, hosing across at the flank, which is
  // the side of the fire rather than its leading edge.
  // Each aims at a point inside the flames on the near side, taken from the
  // fire's own shape rather than guessed. When the aim points were guessed the
  // jets landed on bare hillside instead.
  const crew: { x: number; y: number; aimX: number }[] = [
    { x: parked.x + 12, y: parked.y + 10, aimX: FIRE_X0 + 5 },
    { x: parked.x + 21, y: parked.y + 15, aimX: FIRE_X0 + 13 },
  ];
  for (const c of crew) {
    const cx = Math.round(c.x);
    const cy = Math.round(c.y);
    ctx.drawImage(FIREFIGHTER.canvas as CanvasImageSource, cx, cy - FIREFIGHTER.h);

    // Hose back to the appliance, so it reads as their engine.
    ctx.strokeStyle = '#2a2a34';
    ctx.globalAlpha = 0.8;
    ctx.beginPath();
    ctx.moveTo(cx + 2, cy - 3);
    ctx.lineTo(parkX + ENGINE_BIG.w - 2, parkY + ENGINE_BIG.h - 2);
    ctx.stroke();

    // The jet, arcing into the body of the flames.
    const aimY = flameBase(c.aimX) - 16 + Math.sin(t * 3 + c.aimX) * 3;
    const nozzleX = cx + 5;
    const nozzleY = cy - 6;
    ctx.strokeStyle = C.cyanB;
    ctx.globalAlpha = 0.8;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(nozzleX, nozzleY);
    // A shallow arc rather than a straight line: water is thrown, not aimed.
    ctx.quadraticCurveTo((nozzleX + c.aimX) / 2, Math.min(nozzleY, aimY) - 7, c.aimX, aimY);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Steam where it lands.
    if (Math.floor(t * 6 + c.aimX) % 2 === 0) {
      px(ctx, c.aimX - 1, aimY - 2, 3, 3, '#d7d7d7');
      px(ctx, c.aimX + 1, aimY - 4, 2, 2, '#ffffff');
    }
  }

  // --- Helicopter, bucket swinging, holding over the assembly point --------
  const hx = 252 + Math.sin(t * 0.7) * 13;
  const hy = 26 + Math.sin(t * 1.4) * 4;
  const swing = Math.sin(t * 1.9) * 4;
  ctx.strokeStyle = '#2a2a34';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(hx + 9, hy + 7);
  ctx.lineTo(hx + 9 + swing, hy + 22);
  ctx.stroke();
  px(ctx, hx + 7 + swing, hy + 22, 5, 5, '#2a2a34');
  px(ctx, hx + 8 + swing, hy + 23, 3, 3, C.blueB);
  ctx.drawImage(HELI_BIG.canvas as CanvasImageSource, Math.round(hx), Math.round(hy));
  const rw = 15 + Math.sin(t * 26) * 5;
  ctx.strokeStyle = C.whiteB;
  ctx.globalAlpha = 0.8;
  ctx.beginPath();
  ctx.moveTo(hx + 9 - rw, hy - 1.5);
  ctx.lineTo(hx + 9 + rw, hy - 1.5);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // --- Rescued villagers on the summit, cheering ---------------------------
  for (let i = 0; i < VILLAGERS.length; i++) {
    const vx = 268 + i * 13;
    const vy = ridge(vx) + 2 + (Math.floor(t * 4 + i) % 2 === 0 ? 0 : -1);
    ctx.drawImage(VILLAGERS[i].canvas as CanvasImageSource, vx, vy - VILLAGERS[i].h);
  }
  // A crew member with them, and the assembly-point H.
  ctx.drawImage(FIREFIGHTER.canvas as CanvasImageSource, 253, ridge(253) + 2 - FIREFIGHTER.h);
  px(ctx, 264, ridge(264) + 3, 1, 6, C.whiteB);
  px(ctx, 269, ridge(269) + 3, 1, 6, C.whiteB);
  px(ctx, 264, ridge(266) + 5, 6, 1, C.whiteB);

  // --- Title ---------------------------------------------------------------
  drawFlame(ctx, 12, 14, 14, 2);
  const scale = 4;
  const tx = 48;
  const ty = 18;
  drawPixelText(ctx, GAME_TITLE, tx + 2, ty + 2, scale, '#3a0000');
  drawPixelText(ctx, GAME_TITLE, tx, ty, scale, C.yellowB);
  drawPixelText(ctx, GAME_SUBTITLE, tx + 1, ty + 7 * scale + 7, 1, '#000000');
  drawPixelText(ctx, GAME_SUBTITLE, tx, ty + 7 * scale + 6, 1, C.cyanB);

  // --- Attract prompt ------------------------------------------------------
  if (Math.floor(t * 1.6) % 2 === 0) {
    const prompt = audioBlocked ? 'CLICK ANYWHERE FOR SOUND' : 'SELECT AN INCIDENT';
    const pw = textWidth(prompt, 2);
    const pxx = Math.round((INTRO_W - pw) / 2);
    drawPixelText(ctx, prompt, pxx + 1, 172, 2, '#000000');
    drawPixelText(ctx, prompt, pxx, 171, 2, C.whiteB);
  }
  // Always on, so the player knows the dashboard is there.
  const hint = 'PRESS P FOR SETTINGS AND CONTROLS';
  const hintX = Math.round((INTRO_W - textWidth(hint, 1)) / 2);
  drawPixelText(ctx, hint, hintX + 1, 190, 1, '#000000');
  drawPixelText(ctx, hint, hintX, 189, 1, C.cyanB);

  // --- Scanlines, because it is an attract screen --------------------------
  ctx.globalAlpha = 0.14;
  ctx.fillStyle = '#000000';
  for (let y = 0; y < INTRO_H; y += 2) ctx.fillRect(0, y, INTRO_W, 1);
  ctx.globalAlpha = 1;
}
